// ============================================================================
// Ask AI's subcontractor card — a step on one sub (item #944, the subs part;
// TCSD-0531 one tool per part, saved through the screen's own function). The
// server half is supabase/functions/am-copilot/subs.ts.
//
// Every save here is the function the Subcontractors screens call
// (src/modules/subcontractors/hub/actions.ts): the pipeline board's move
// (moveSubOnPipeline), Start paperwork now (startPaperwork), Mark inactive
// (markInactive), Return to bench (returnToBench), Make / Remove Tier 1 (setSubTier1).
// ============================================================================

import {
  markInactive,
  moveSubOnPipeline,
  returnToBench,
  setSubTier1,
  startPaperwork,
} from '../subcontractors/hub/actions';
import { refreshReaders } from '../../shared/save-path';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { subButtonWords } from '../../../supabase/functions/am-copilot/buttons';
import type { FieldSpec } from './building-actions';

export const SUB_ACTIONS = ['move_stage', 'start_paperwork', 'mark_inactive', 'return_to_bench', 'set_tier_1'] as const;
export type SubAction = (typeof SUB_ACTIONS)[number];

/** A draft as the server persisted it (prepareSubAction). */
export interface SubDraft {
  provider_id: string;
  action: SubAction;
  fields: Record<string, unknown>;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface SubCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string | null;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const blank = (v: unknown) => str(v).trim().length === 0;
const ctxOf = (d: SubDraft) => (d.context ?? {}) as Record<string, unknown>;
const WORDS: Record<string, string> = { Lead: 'Lead', Meet: 'Interviewing', Ready: 'Bench' };

/** The sub as the screens name them. */
export const subOf = (d: SubDraft) => str(ctxOf(d).name) || str(d.display_name) || d.provider_id;

/** What the card shows for this draft, with the person's edits `f`. */
export function subCard(d: SubDraft, f: Record<string, unknown>): SubCardSpec {
  const c = ctxOf(d);
  const now = { label: 'Now', value: str(c.status_words) || '—' };
  const base: SubCardSpec = { title: '', fields: [], info: [now], readBack: null, doItLabel: subButtonWords(d.action, f), missing: null };
  switch (d.action) {
    case 'move_stage':
      return { ...base, title: `Move to ${WORDS[str(f.to)] ?? str(f.to)}` };
    case 'start_paperwork':
      return { ...base, title: 'Start paperwork now', readBack: `This opens ${subOf(d)}'s paperwork card for HR, and HR is told.` };
    case 'mark_inactive':
      return { ...base, title: 'Mark inactive', readBack: `This marks ${subOf(d)} inactive. They come back only through Return to bench.` };
    case 'return_to_bench':
      return {
        ...base,
        title: 'Return to bench',
        fields: [{ key: 'why', label: 'Why are they coming back? (kept in their notes)', kind: 'textarea', required: true }],
        missing: blank(f.why) ? 'One line on why they are coming back, please.' : null,
      };
    case 'set_tier_1':
      return { ...base, info: [], title: f.on === false ? 'Remove Tier 1' : 'Make Tier 1' };
  }
}

export interface SubCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}
const fail = (error: string): SubCommitResult => ({ ok: false, error });

/** Save one sub draft through the Subcontractors screen's own function. */
export async function commitSubAction(d: SubDraft, f: Record<string, unknown>, eid: string | null): Promise<SubCommitResult> {
  const missing = subCard(d, f).missing;
  if (missing) return fail(missing);
  if (!eid) return fail('Your sign-in has no employee record, so nothing can be saved in your name.');
  const id = d.provider_id;
  const name = subOf(d);
  const ok = (note: string, committed_payload: Record<string, unknown>, table = 'service_providers'): SubCommitResult =>
    ({ ok: true, note, target_table: table, target_row_id: id, committed_payload });
  try {
    switch (d.action) {
      case 'move_stage':
        await moveSubOnPipeline(id, str(f.to));
        return ok(`${name} → ${WORDS[str(f.to)] ?? str(f.to)}.`, { provider_status: str(f.to) });
      case 'start_paperwork': {
        const out = await startPaperwork(id);
        refreshReaders(['partner_onboarding']);
        return ok(out.created ? 'Paperwork started — HR has been notified.' : 'Paperwork was already started.', { started: out.created }, 'partner_onboarding');
      }
      case 'mark_inactive':
        await markInactive(id, eid);
        return ok(`${name} — marked inactive.`, { provider_status: 'Inactive' });
      case 'return_to_bench':
        await returnToBench(id, str(f.why), str(ctxOf(d).me_name) || null);
        return ok(`${name} — back on the bench.`, { provider_status: 'Ready', why: str(f.why).trim() });
      case 'set_tier_1':
        await setSubTier1(id, f.on !== false);
        return ok(f.on === false ? `${name} is no longer Tier 1.` : `${name} is now Tier 1.`, { tier_1: f.on !== false });
    }
  } catch (e) {
    return fail(rpcErrorMessage(e, 'That did not save.'));
  }
}
